import { BEFORE_CONNECTED_CALLBACK, HOST_ELEMENT_KEY } from './renderer';

/**
 * The {@link OutputEvent} decorator is a shortcut for dispatching Custom Events
 * on the component or directive. Note that this is just an implementation of
 * the browser's native DOM Events.
 *
 * When no arguments are provided it will use the property's name as event name.
 * You can change this behavior by providing a string as the first argument:
 * ```typescript
 * @OutputEvent('EventName')
 * ```
 *
 * Alternatively an options object can be provided as first argument:
 * ```typescript
 * @OutputEvent({
 *  name: 'EventName', // default => [propertyKey]
 *  bubbles: true, // default => true
 *  cancelable: true, // default => true
 * });
 * ```
 *
 * In the example below we're creating a new 'EventEmitter' property which we'll
 * use to notify surrounding elements about our vote. A click on the component
 * is in this case considered to be a vote.
 *
 * ```typescript
 * @DomComponent({
 *  selector: 'aa-vote',
 *  template: `<span>Click to vote on {{identifier}}!</span>`
 * })
 * class VoterComponent {
 *  @Attribute('data-vote-id')
 *  private identifier: string;
 *
 *  @OutputEvent('VOTE')
 *  private readonly voted: EventEmitter<boolean>
 *
 *  @HostEvent('click')
 *  onClick(e: Event) {
 *    this.voted.emit(this.identifier);
 *  }
 * }
 * ```
 *
 * Surrounding our `aa-voter` we have a `aa-vote-collector` component. This component listens
 * for vote events, dispatched by `aa-vote`, and collects them under a local data property.
 *
 * ```typescript
 * @DomComponent({
 *  selector: 'aa-vote-collector',
 *  template: `
 * <div class="vote-wrapper">
 *  <aa-vote identifier='Dev 01'></aa-vote>
 *  <aa-vote identifier='Dev 02'></aa-vote>
 * </div>`
 * });
 * class VoteCollectorComponent {
 *  private voteData = {};
 *
 *  @HostEvent('VOTE')
 *  onVote(e: CustomEvent) {
 *    const voteId = e.detail;
 *    this.voteData[voteId] = (this.voteData[voteId]) ? 1 : this.voteData[voteId] + 1;
 *  }
 * }
 * ```
 *
 * @param event
 * @param userOptions
 */
export const OutputEvent = (eventOptions?: string | Partial<EventOptions>): PropertyDecorator => {
  return function (target: any, propertyKey: string | symbol) {
    // Use the first argument when it's a string, otherwise check if the name option
    // has been provided and fall-back to the propertyKey.
    const normalizedEventName = (typeof eventOptions === 'string')
      ? eventOptions : (eventOptions && eventOptions.name)
        ? eventOptions.name : String(propertyKey);

    // Normalize options
    const options: EventOptions = {
      name: normalizedEventName,
      bubbles: true,
      cancelable: true,
      ...(eventOptions && typeof eventOptions !== 'string') ? eventOptions : {},
    };

    // Adds the 'emit' method during the 'BeforeConnection' chain
    const beforeConnectedCallback = target[BEFORE_CONNECTED_CALLBACK] || function () { };
    target[BEFORE_CONNECTED_CALLBACK] = function () {
      const hostElm: HTMLElement = this[HOST_ELEMENT_KEY]();

      // Define a property with a 'emit' method which dispatches
      // a new customevent on the host element
      Object.defineProperty(this, propertyKey, {
        value: {
          emit: function (data: any) {
            const evt = document.createEvent('CustomEvent');

            evt.initCustomEvent(
              options.name,
              options.bubbles,
              options.cancelable,
              data,
            );
            hostElm.dispatchEvent(evt);
          },
        },
        writable: false,
        configurable: false,
        enumerable: false,
      });

      beforeConnectedCallback.call(this);
    };
  };
};

export interface EventOptions {
  name: string;
  bubbles: boolean;
  cancelable: boolean;
}

export interface EventEmitter<T = void> {
  emit(data?: T): void;
}
